/**
 * Foundation · Timesheet (M-WF-01) — calcolatore unico degli orari di lavoro (frontend).
 * Stessa specifica di `backend/foundation/timesheet/calc.py`, verificata dagli stessi casi
 * (`__fixtures__/day_cases.json`). Calcoli in MINUTI INTERI; ore solo in uscita.
 * Indipendente dal modulo applicativo: riusabile da Presenze, Turni, Timesheet, Appalti.
 */
export const DAY_MINUTES = 1440;
export const DEFAULT_TIMESHEET_RULES = Object.freeze({
  maxShiftMinutes: 960,
  maxSpanMinutes: DAY_MINUTES,
  largeDeviationMinutes: 240,
  attribution: "start_day",
});
export const DEFAULT_INTERVAL_COUNT = 2;

export const TIMESHEET_MESSAGES = Object.freeze({
  INVALID_TIME: "Orario non valido (usa HH:MM, es. 08:30)",
  INCOMPLETE: "Fascia incompleta: manca entrata o uscita",
  ZERO_DURATION: "Entrata e uscita coincidono",
  OVERLAP: "Fasce sovrapposte",
  SEQUENCE: "Sequenza incoerente: la fascia inizia prima della precedente",
  SHIFT_TOO_LONG: "Turno oltre il limite massimo",
  SPAN_OVER_24H: "Arco giornaliero oltre 24 ore",
  NIGHT_SHIFT: "Turno notturno (attraversa la mezzanotte)",
  WORK_ON_REST_DAY: "Orari in un giorno senza ore teoriche",
  LARGE_DEVIATION: "Forte scostamento dalle ore teoriche",
  JUSTIFICATION_INCOMPLETE: "Giustificativo incompleto",
  JUSTIFIED_OVER_THEORETICAL: "Lavorato + giustificato supera le ore teoriche",
});

/** "8" "830" "0830" "8:30" "8.30" "8,30" → minuti dalla mezzanotte (24:00 ammesso); null se non valido. */
export function parseTime(raw) {
  let s = String(raw ?? "").replace(/\s+/g, "");
  if (!s) return null;
  if (/^\d{3,4}$/.test(s)) s = `${s.slice(0, -2)}:${s.slice(-2)}`;
  const m = s.match(/^(\d{1,2})(?:[:.,](\d{2}))?$/);
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2] || 0);
  if (mi > 59 || h > 24 || (h === 24 && mi > 0)) return null;
  return h * 60 + mi;
}

export const formatTime = (minutes) =>
  minutes === null || minutes === undefined ? "" : `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

/** Normalizza la digitazione: "830" → "08:30"; valori non validi restano invariati. */
export function normalizeTimeInput(raw) {
  const m = parseTime(raw);
  return m === null ? String(raw ?? "").trim() : formatTime(m);
}

const issue = (level, code, interval) => ({ level, code, message: TIMESHEET_MESSAGES[code], ...(interval !== undefined ? { interval } : {}) });
const pair = (iv) => (Array.isArray(iv) ? iv : [iv?.start, iv?.end]);

export function computeDay(intervals, rules = {}) {
  const r = { ...DEFAULT_TIMESHEET_RULES, ...rules };
  const issues = [];
  const placed = [];
  let offset = 0;
  let night = false;
  (intervals || []).forEach((iv, i) => {
    const [startRaw, endRaw] = pair(iv);
    const hasS = String(startRaw ?? "").trim() !== "";
    const hasE = String(endRaw ?? "").trim() !== "";
    if (!hasS && !hasE) return;
    if (hasS !== hasE) { issues.push(issue("error", "INCOMPLETE", i)); return; }
    const s = parseTime(startRaw);
    const e = parseTime(endRaw);
    if (s === null || e === null || s === DAY_MINUTES) { issues.push(issue("error", "INVALID_TIME", i)); return; }
    if (e === s) { issues.push(issue("error", "ZERO_DURATION", i)); return; }
    const crosses = e < s;
    const duration = crosses ? e + DAY_MINUTES - s : e - s;
    if (crosses) night = true;
    if (duration > r.maxShiftMinutes) issues.push(issue("error", "SHIFT_TOO_LONG", i));
    const absS = s + offset;
    if (placed.length) {
      const prev = placed[placed.length - 1];
      if (absS < prev.end) issues.push(issue("error", absS + duration <= prev.start ? "SEQUENCE" : "OVERLAP", i));
    }
    placed.push({ i, start: absS, end: absS + duration });
    if (absS + duration >= DAY_MINUTES) offset = DAY_MINUTES;
  });
  const worked = placed.reduce((t, p) => t + (p.end - p.start), 0);
  let pause = 0;
  for (let k = 1; k < placed.length; k++) pause += Math.max(0, placed[k].start - placed[k - 1].end);
  if (placed.length && placed[placed.length - 1].end - placed[0].start > r.maxSpanMinutes) issues.push(issue("error", "SPAN_OVER_24H"));
  if (night) issues.push(issue("info", "NIGHT_SHIFT"));
  return { workedMinutes: worked, pauseMinutes: pause, night, issues };
}

/** Giornata completa (orari + giustificativi) rispetto alle ore teoriche (minuti). */
export function evaluateDay(day, theoreticalMinutes = null, rules = {}) {
  const r = { ...DEFAULT_TIMESHEET_RULES, ...rules };
  const res = computeDay(day?.intervals || [], r);
  let justified = 0;
  (day?.justifications || []).forEach((j) => {
    if (!j?.cause || !(Number(j?.minutes) > 0)) { res.issues.push(issue("error", "JUSTIFICATION_INCOMPLETE")); return; }
    justified += Math.round(Number(j.minutes));
  });
  res.justifiedMinutes = justified;
  if (theoreticalMinutes !== null && theoreticalMinutes !== undefined) {
    const total = res.workedMinutes + justified;
    if (theoreticalMinutes === 0 && res.workedMinutes > 0) res.issues.push(issue("warning", "WORK_ON_REST_DAY"));
    if (justified && total > theoreticalMinutes && theoreticalMinutes > 0
        && justified > Math.max(0, theoreticalMinutes - res.workedMinutes)) res.issues.push(issue("warning", "JUSTIFIED_OVER_THEORETICAL"));
    if (theoreticalMinutes > 0 && total > 0 && Math.abs(total - theoreticalMinutes) > r.largeDeviationMinutes) res.issues.push(issue("warning", "LARGE_DEVIATION"));
    res.differenceMinutes = total - theoreticalMinutes;
  }
  return res;
}

export const hasErrors = (res) => (res?.issues || []).some((i) => i.level === "error");
export const minutesToHours = (m) => (Number(m) || 0) / 60;
export const hoursToMinutes = (h) => Math.round((Number(h) || 0) * 60);

/** Giornata vuota con N fasce. */
export const emptyDay = (date, count = DEFAULT_INTERVAL_COUNT) => ({
  date, intervals: Array.from({ length: count }, () => ({ start: "", end: "" })), justifications: [], note: "",
});

export const isDayEmpty = (d) =>
  !(d?.intervals || []).some((iv) => String(iv.start || "").trim() || String(iv.end || "").trim())
  && !(d?.justifications || []).length;

/** Copia gli orari (non la data) da una giornata ad un'altra. */
export const copyDayContent = (from, to) => ({
  ...to,
  intervals: (from?.intervals || []).map((iv) => ({ ...iv })),
  justifications: (from?.justifications || []).map((j) => ({ ...j })),
});

/** Incolla multiplo da Excel: righe separate da newline, celle da TAB → matrice di stringhe. */
export const parseClipboardGrid = (text) =>
  String(text || "").replace(/\r/g, "").split("\n").filter((l, i, a) => l !== "" || i < a.length - 1).map((l) => l.split("\t"));
